import { format, subDays } from 'date-fns';
import { resolveDayOff } from '@/domain/artistAdmin';
import type { Artist, Booking } from '@/domain/types';

/** Minutes that make a calendar day a "full ~8h day" for an artist. */
export const FULL_DAY_MINUTES = 480;
/** Safety bound on the backward walk so it always terminates. */
const LOOKBACK_DAYS = 90;

/** Statuses that represent real (scheduled or worked) chair time. */
const COUNTED_STATUSES: ReadonlySet<Booking['status']> = new Set([
  'confirmed', 'checked-in', 'in-chair', 'completed',
]);

export function isCountableBooking(b: Booking): boolean {
  return COUNTED_STATUSES.has(b.status);
}

/**
 * Did the artist work a full ~8h day on this calendar day? True if a `full-day`
 * sitting is booked, or the counted sittings total ≥ 8h. A short day (e.g. 3h)
 * is not a full day.
 */
export function isFullDayWorked(bookingsOnDay: Booking[]): boolean {
  const counted = bookingsOnDay.filter(isCountableBooking);
  if (counted.some((b) => b.sessionLength === 'full-day')) return true;
  const totalMin = counted.reduce((sum, b) => sum + b.durationMin, 0);
  return totalMin >= FULL_DAY_MINUTES;
}

/**
 * Is this single booking a full-day session? A `full-day` sitting, or a session
 * of >= 8h. Unlike `isFullDayWorked` (which totals a whole day's chair time),
 * this asks about ONE booking - the unit a double day is counted in.
 * // ASSUMPTION: a "full-day session" = sessionLength 'full-day' OR durationMin >= FULL_DAY_MINUTES (480). The nominal 11-6 block is 7h; tag such sittings 'full-day' to count. Confirm the threshold with the client.
 */
export function isFullDaySession(b: Booking): boolean {
  if (!isCountableBooking(b)) return false;
  return b.sessionLength === 'full-day' || b.durationMin >= FULL_DAY_MINUTES;
}

/**
 * A "double day" (locked 14 Jul definition): a single calendar day on which the
 * artist works TWO full-day sessions - the nominal day block (11-6) plus an
 * evening block (6pm onward). Full days on DIFFERENT calendar days are NOT a
 * double day; the earlier "run of N consecutive full days = N-1 doubles" rule
 * is rejected.
 */
export function isDoubleDay(bookingsOnDay: Booking[]): boolean {
  return bookingsOnDay.filter(isFullDaySession).length >= 2;
}

function groupByDay(artistId: string, bookings: Booking[]): Map<string, Booking[]> {
  const byDay = new Map<string, Booking[]>();
  for (const b of bookings) {
    if (b.artistId !== artistId) continue;
    const key = format(new Date(b.start), 'yyyy-MM-dd');
    const list = byDay.get(key);
    if (list) list.push(b);
    else byDay.set(key, [b]);
  }
  return byDay;
}

/**
 * Total double days since the artist's last day off. Walks back from `asOfIso`,
 * counting each calendar day that is a double day (two full-day sessions on that
 * day), and stops at the first rostered day off (weekly rest, RDO, comp, leave —
 * see `resolveDayOff`), which bounds "since last day off".
 */
export function countDoubleDaysSinceLastDayOff(artist: Artist, bookings: Booking[], asOfIso: string): number {
  const byDay = groupByDay(artist.id, bookings);
  let doubleDays = 0;
  let cursor = new Date(asOfIso);
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    if (resolveDayOff(artist.availability, cursor) !== null) return doubleDays;
    if (isDoubleDay(byDay.get(format(cursor, 'yyyy-MM-dd')) ?? [])) doubleDays += 1;
    cursor = subDays(cursor, 1);
  }
  return doubleDays;
}

/**
 * The calendar date (yyyy-MM-dd) of the most recent double day not already
 * referenced by a comp block's `compensatesDate`. Used to pre-fill the comp-day
 * note. Returns null if there is none since the last day off.
 */
export function mostRecentUncompensatedDoubleDay(artist: Artist, bookings: Booking[], asOfIso: string): string | null {
  const compensated = new Set(
    artist.availability.leaveBlocks
      .filter((b) => b.type === 'comp' && b.compensatesDate)
      .map((b) => b.compensatesDate!),
  );
  const byDay = groupByDay(artist.id, bookings);
  let cursor = new Date(asOfIso);
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    if (resolveDayOff(artist.availability, cursor) !== null) break;
    const key = format(cursor, 'yyyy-MM-dd');
    if (isDoubleDay(byDay.get(key) ?? []) && !compensated.has(key)) return key;
    cursor = subDays(cursor, 1);
  }
  return null;
}

/** The weekly session budget (full-day session units). Display/warning only. */
// ASSUMPTION: budget constant = 5, client-adjustable later.
export const WEEKLY_SESSION_BUDGET = 5;

/**
 * Full-day session units the artist has this week across `weekDays` (each a
 * yyyy-MM-dd or ISO date). A double day contributes 2 because it has two
 * full-day sessions. Display/warning only - comp days remain manual.
 */
export function sessionsThisWeek(artist: Artist, bookings: Booking[], weekDays: readonly string[]): number {
  const days = new Set(weekDays.map((d) => d.slice(0, 10)));
  const byDay = groupByDay(artist.id, bookings);
  let units = 0;
  for (const [key, dayBookings] of byDay) {
    if (!days.has(key)) continue;
    units += dayBookings.filter(isFullDaySession).length;
  }
  return units;
}

export type DoubleDaySeverity = 'none' | 'warning' | 'critical';

export function doubleDaySeverity(count: number): DoubleDaySeverity {
  if (count >= 2) return 'critical';
  if (count === 1) return 'warning';
  return 'none';
}
